import React, { useState, useMemo } from 'react';

import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import ListPageHeader from '@shared/ListPageHeader';
import TableIndex from '@shared/table/TableIndex';
import type { SupportRule } from '@shared/table/TableIndex';
import { MultipleValue } from '@shared/table/Rules/component';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useIsStandard } from '@contexts/operator/PlanContext';

import { billingService } from '@services/operator/billingService';
import { formatDateTime } from '../../../utils/dateTime';

const STATUS_COLORS: Record<string, string> = {
  open: 'blue',
  closed: 'orange',
  invoiced: 'green',
};

interface MerchantOption {
  id: string;
  name: string;
}

interface BillingRow {
  id: string;
  name?: string;
  merchant_name?: string;
  period_start?: string;
  period_end?: string;
  status?: string;
  total_amount?: string | number;
  items_count?: number;
  created_at?: string;
  [key: string]: unknown;
}

export default function BillingsList() {
  const { t } = useTranslation();
  const { merchants } = useOperatorData() as { merchants: MerchantOption[] };
  const isStandard = useIsStandard();
  const [lastModified, setLastModified] = useState(0);

  const merchantOptions = useMemo(() => merchants.map((m: MerchantOption) => ({
    label: m.name,
    value: m.id,
  })), [merchants]);

  const columns = useMemo(() => [
    // 單一 merchant 模式下不顯示 merchant 欄位
    ...(isStandard ? [] : [{
      nameKey: 'operator.billings.list.columns.merchant_name',
      sortable: true,
      sortFieldName: 'merchants.name',
      width: '160px',
      wrap: true,
      cell: ({ merchant_name }: BillingRow) => merchant_name || '-',
    }]),
    {
      nameKey: 'operator.billings.list.columns.name',
      persist: true,
      width: '200px',
      wrap: true,
      cell: ({ id, name }: BillingRow) => (
        <Link to={`/billings/${id}`}>
          {name}
        </Link>
      ),
    },
    {
      nameKey: 'operator.billings.list.columns.period',
      sortable: true,
      sortFieldName: 'billings.period_start',
      width: '200px',
      cell: ({ period_start, period_end }: BillingRow) => `${period_start} ~ ${period_end}`,
    },
    {
      nameKey: 'operator.billings.list.columns.status',
      width: '120px',
      cell: ({ status }: BillingRow) => (
        <Tag color={status ? STATUS_COLORS[status] : undefined}>
          {t(`operator.billings.statuses.${status}`)}
        </Tag>
      ),
    },
    {
      nameKey: 'operator.billings.list.columns.total_amount',
      sortable: true,
      sortFieldName: 'billings.total_amount',
      width: '140px',
      cell: ({ total_amount }: BillingRow) =>
        Number(total_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
    {
      nameKey: 'operator.billings.list.columns.items_count',
      width: '100px',
      cell: ({ items_count }: BillingRow) => items_count,
    },
    {
      nameKey: 'operator.billings.list.columns.created_at',
      sortable: true,
      sortFieldName: 'billings.created_at',
      width: '180px',
      wrap: true,
      cell: ({ created_at }: BillingRow) => created_at ? formatDateTime(created_at) : '-',
    },
  ], [t, isStandard]);

  const supportRules = useMemo<SupportRule[]>(() => [
    // 單一 merchant 模式下不顯示 merchant 篩選器
    ...(isStandard ? [] : [{
      label: t('shared.merchant'),
      component: MultipleValue,
      inputName: 'merchant_id',
      defaultOptions: merchantOptions,
      createable: false,
    }]),
    {
      label: t('operator.billings.fields.status'),
      component: MultipleValue,
      inputName: 'status',
      defaultOptions: ['open', 'closed', 'invoiced'].map((s) => ({
        value: s,
        label: t(`operator.billings.statuses.${s}`),
      })),
    },
  ], [t, merchantOptions, isStandard]);

  return (
    <div>
      <ListPageHeader
        items={['merchant_settings', 'merchant_settings.billings']}
      />
      <TableIndex
        columns={columns}
        service={billingService}
        searchable={{
          match: 'contains',
          searchFields: [
            t('operator.billings.list.columns.merchant_name'),
            t('operator.billings.list.columns.name'),
          ],
        }}
        columnsEditable
        tableId="OperatorBillingsTable"
        defaultQuery={{
          sorting_field: 'billings.period_start',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        actions={[]}
      />
    </div>
  );
}
